import React, { useEffect, useState } from 'react';
import { Box, Dialog, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import { useTheme, useMediaQuery } from '@mui/material';
import { z } from 'zod';
import { AppDispatch } from '../../stateManagement/store';
import { useDispatch } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import { IServiceProvider, IServiceProviderRequest } from "@ontime/types";
import { createServiceProviderAsync, updateServiceProviderAsync } from '../../stateManagement/serviceProviderSlice';
import { useSelector } from 'react-redux';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';

export const serviceProviderSchema = z.object({
    serviceProviderId: z.string(),
    name: z.string().min(1, "Name cannot be empty.").max(100, "Maximum length of Name is 100 characters."),
    specialty: z.string().max(200, "Maximum length of Address is 200 characters.").optional(),
    description: z.string().max(500, "Maximum length of Address is 500 characters.").optional(),
    address: z.string().max(500, "Maximum length of Address is 500 characters.").optional(),
    phone: z.string()
        .max(20, "Maximum length of Phone is 20 characters.")
        .regex(/^\d*$/, "Phone number must contain only digits.")
        .optional(),
    email: z.string()
        .max(100, "Maximum length of Email is 100 characters.")
        .email("Invalid email format.")
        .optional(),
    isPublic: z.boolean(),
});

const ServiceProviderDialog: React.FC<{
    state: { open: boolean; serviceProviderInput?: IServiceProvider; };
    closeServiceProviderDialog: (input?: IServiceProvider) => void;
}> = ({ state, closeServiceProviderDialog }) => {

    const organization = useSelector(selectSelectedOrganization);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch<AppDispatch>();


    const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<IServiceProviderRequest>({
        resolver: zodResolver(serviceProviderSchema),
        defaultValues: {
            serviceProviderId: '',
            name: '',
            isPublic: true,
        }
    });


    const handleServiceProvider = async (data: IServiceProviderRequest) => {
        setLoading(true);
        try {
            let result;
            if (state.serviceProviderInput?.serviceProviderId) {
                result = await dispatch(updateServiceProviderAsync({ organizationId: organization.organizationId ?? '', data })).unwrap();
            } else {
                result = await dispatch(createServiceProviderAsync({ organizationId: organization.organizationId ?? '', data })).unwrap();
            }
            if (result) {
                closeServiceProviderDialog();
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (state.serviceProviderInput?.serviceProviderId) {
            reset({
                serviceProviderId: state.serviceProviderInput?.serviceProviderId,
                name: state.serviceProviderInput.name ?? '',
                specialty: state.serviceProviderInput.specialty ?? '',
                description: state.serviceProviderInput.description ?? '',
                address: state.serviceProviderInput.address ?? '',
                phone: state.serviceProviderInput.phone ?? '',
                email: state.serviceProviderInput.email ?? '',
                isPublic: state.serviceProviderInput.isPublic as boolean,
            });
        }
        else {
            reset({
                serviceProviderId: '',
                name: '',
                isPublic: true,
            });
        }
    }, [state, reset]);

    return (
        <Dialog
            open={state.open}
            onClose={() => closeServiceProviderDialog()}
            fullWidth
            fullScreen={fullScreen}
        >
            <CustomDialogTitle handleClose={closeServiceProviderDialog} title={`${state.serviceProviderInput?.serviceProviderId ? 'Updated' : 'New'} service provider`} loading={loading} />
            <CustomDialogContent>
                <form onSubmit={handleSubmit(handleServiceProvider)} noValidate>
                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Name"
                            InputLabelProps={{ shrink: true }}
                            {...register('name')}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Specialty"
                            InputLabelProps={{ shrink: true }}
                            {...register('specialty')}
                            error={!!errors.specialty}
                            helperText={errors.specialty?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Description"
                            InputLabelProps={{ shrink: true }}
                            {...register('description')}
                            error={!!errors.description}
                            helperText={errors.description?.message}
                            multiline
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Address"
                            InputLabelProps={{ shrink: true }}
                            {...register('address')}
                            error={!!errors.address}
                            helperText={errors.address?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Phone"
                            InputLabelProps={{ shrink: true }}
                            {...register('phone')}
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                        />
                    </Box>
                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Email"
                            InputLabelProps={{ shrink: true }}
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                        />
                    </Box>
                    <Box mt={2}>
                        <FormControl fullWidth error={!!errors.isPublic}>
                            <InputLabel>Accessible</InputLabel>
                            <Select
                                label="Accessible"
                                value={watch('isPublic') ?? true} // fallback to `true` instead of ''
                                onChange={(e) => setValue('isPublic', e.target.value === 'true')}
                            >
                                <MenuItem value="true">Public</MenuItem>
                                <MenuItem value="false">Private</MenuItem>
                            </Select>
                            <FormHelperText>{errors.isPublic?.message}</FormHelperText>
                        </FormControl>
                    </Box>

                    <CustomFormAction>
                        <CustomButton lable='Save' type='submit' disabled={loading} infoContent={`Save the ${state.serviceProviderInput?.serviceProviderId ? 'updated' : 'new'} service provider record`} />
                    </CustomFormAction>
                </form>
            </CustomDialogContent>
        </Dialog>
    );
};

export default ServiceProviderDialog;